import { Routes } from '@angular/router';
import { adminGuard } from './core/guards/admin.guard';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { organizationContextGuard } from './core/guards/organization-context.guard';
import { orgAdminGuard, superAdminGuard } from './core/guards/org-management.guard';
import { profileGuard } from './core/guards/profile.guard';
import { AppShellComponent } from './layout/app-shell/app-shell.component';

export const routes: Routes = [
  {
    path: '',
    component: AppShellComponent,
    children: [
      {
        path: '',
        loadComponent: () => import('./features/public/home/home').then((m) => m.HomeComponent),
      },
      {
        path: 'espacios',
        loadComponent: () => import('./features/public/spaces/spaces').then((m) => m.SpacesComponent),
      },
      {
        path: 'espacios/:slug',
        loadComponent: () =>
          import('./features/public/space-detail/space-detail').then((m) => m.SpaceDetailComponent),
      },
      {
        path: 'login',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./features/public/login/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'register',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./features/public/register/register.component').then((m) => m.RegisterComponent),
      },
      {
        path: 'verify-email/:id/:hash',
        loadComponent: () =>
          import('./features/public/verify-email/verify-email.component').then(
            (m) => m.VerifyEmailComponent,
          ),
      },
      {
        path: 'auth/activate-account/:id/:hash',
        loadComponent: () =>
          import('./features/public/activate-account/activate-account.component').then(
            (m) => m.ActivateAccountComponent,
          ),
      },
      {
        path: 'forgot-password',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./features/public/forgot-password/forgot-password.component').then(
            (m) => m.ForgotPasswordComponent,
          ),
      },
      {
        path: 'reset-password',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./features/public/reset-password/reset-password.component').then(
            (m) => m.ResetPasswordComponent,
          ),
      },
      {
        path: 'perfil',
        canActivate: [authGuard, profileGuard],
        loadComponent: () =>
          import('./features/account/profile/profile.component').then((m) => m.ProfileComponent),
      },
      {
        path: 'mis-reservas',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/reservations/reservations-list.component').then(
            (m) => m.ReservationsListComponent,
          ),
      },
      {
        path: 'mis-reservas/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/reservations/reservation-detail.component').then(
            (m) => m.ReservationDetailComponent,
          ),
      },
      {
        path: 'notificaciones',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/notifications/notifications-list.component').then(
            (m) => m.NotificationsListComponent,
          ),
      },
      {
        path: 'reservas/nueva',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/public/create-reservation/create-reservation.component').then(
            (m) => m.CreateReservationComponent,
          ),
      },
      {
        path: 'privacidad',
        loadComponent: () =>
          import('./features/public/static/privacy/privacy').then((m) => m.PrivacyComponent),
      },
      {
        path: 'terminos',
        loadComponent: () =>
          import('./features/public/static/terms/terms').then((m) => m.TermsComponent),
      },
      {
        path: 'terminos-y-condiciones',
        redirectTo: 'terminos',
        pathMatch: 'full',
      },
      {
        path: 'aviso-legal',
        loadComponent: () =>
          import('./features/public/static/legal/legal').then((m) => m.LegalComponent),
      },
      {
        path: 'cookies',
        loadComponent: () =>
          import('./features/public/static/cookies/cookies').then((m) => m.CookiesComponent),
      },
      {
        path: 'ayuda',
        loadComponent: () =>
          import('./features/public/help/help').then((m) => m.HelpComponent),
      },
      {
        path: 'recuperar',
        redirectTo: 'forgot-password',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'admin/select-organization',
    canActivate: [adminGuard],
    loadComponent: () =>
      import('./features/admin/select-organization/admin-select-organization.component').then(
        (m) => m.AdminSelectOrganizationComponent,
      ),
  },
  {
    path: 'admin',
    canActivate: [adminGuard],
    canActivateChild: [organizationContextGuard],
    loadComponent: () =>
      import('./layout/admin-layout/admin-layout.component').then((m) => m.AdminLayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/admin/dashboard/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent,
          ),
      },
      {
        path: 'users',
        canActivate: [orgAdminGuard],
        loadComponent: () =>
          import('./features/admin/users/admin-users.component').then((m) => m.AdminUsersComponent),
      },
      {
        path: 'manager-groups',
        loadComponent: () =>
          import('./features/admin/manager-groups/admin-manager-groups.component').then(
            (m) => m.AdminManagerGroupsComponent,
          ),
      },
      {
        path: 'manager-groups/:id',
        loadComponent: () =>
          import('./features/admin/manager-group-detail/admin-manager-group-detail.component').then(
            (m) => m.AdminManagerGroupDetailComponent,
          ),
      },
      {
        path: 'organizations',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/organizations/admin-organizations.component').then(
            (m) => m.AdminOrganizationsComponent,
          ),
      },
      {
        path: 'spaces',
        loadComponent: () =>
          import('./features/admin/spaces/admin-spaces.component').then((m) => m.AdminSpacesComponent),
      },
      {
        path: 'spaces/new',
        loadComponent: () =>
          import('./features/admin/space-form/admin-space-form.component').then(
            (m) => m.AdminSpaceFormComponent,
          ),
      },
      {
        path: 'spaces/:id/edit',
        loadComponent: () =>
          import('./features/admin/space-form/admin-space-form.component').then(
            (m) => m.AdminSpaceFormComponent,
          ),
      },
      {
        path: 'spaces/:id',
        loadComponent: () =>
          import('./features/admin/space-form/admin-space-form.component').then(
            (m) => m.AdminSpaceFormComponent,
          ),
      },
      {
        path: 'services',
        loadComponent: () =>
          import('./features/admin/services/admin-services.component').then(
            (m) => m.AdminServicesComponent,
          ),
      },
      {
        path: 'availability',
        loadComponent: () =>
          import('./features/admin/availability/admin-availability.component').then(
            (m) => m.AdminAvailabilityComponent,
          ),
      },
      {
        path: 'reservations',
        loadComponent: () =>
          import('./features/admin/reservations/admin-reservations.component').then(
            (m) => m.AdminReservationsComponent,
          ),
      },
      {
        path: 'reservations/new',
        data: { adminCreation: true },
        loadComponent: () =>
          import('./features/admin/reservation-create/admin-reservation-create.component').then(
            (m) => m.AdminReservationCreateComponent,
          ),
      },
      {
        path: 'reservations/:id',
        loadComponent: () =>
          import('./features/admin/reservation-detail/admin-reservation-detail.component').then(
            (m) => m.AdminReservationDetailComponent,
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/admin/settings/admin-settings.component').then(
            (m) => m.AdminSettingsComponent,
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/admin/profile/admin-profile.component').then(
            (m) => m.AdminProfileComponent,
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];
